import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { getFunctions, httpsCallable } from 'firebase/functions';
import CreateProjectModal from '../common/CreateProjectModal';

// Component imports
import ProjectsHeader from './ProjectsHeader';
import ProjectsSummaryCards from './ProjectsSummaryCards';
import ProjectsFilters from './ProjectsFilters';
import ProjectsGrid from './ProjectsGrid';
import ProjectsEmptyState from './ProjectsEmptyState';
import ProjectsLoadingState from './ProjectsLoadingState';
import ProjectsErrorState from './ProjectsErrorState';

import styles from './Projects.module.css';

const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [activeFilter, setActiveFilter] = useState('in_progress');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNewProjectMenu, setShowNewProjectMenu] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState({});
  const { jeweller } = useAuth();
  const navigate = useNavigate();
  const functions = getFunctions();

  const walkthrough = {
    title: 'How the Projects page works',
    durationLabel: '2 min',
    embedUrl: 'https://player.vimeo.com/video/1170342700?badge=0&autopause=0&player_id=0&app_id=58479'
  };

  const fetchProjects = async () => {
    if (!jeweller?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const projectsQuery = query(
        collection(db, 'projects'),
        where('jewellerId', '==', jeweller.id)
      );
      
      const projectSnapshot = await getDocs(projectsQuery);
      
      const projectData = projectSnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
          customer: doc.data().customerInfo
        }))
        .filter(project => 
          project.status === 'project_started' || 
          project.status === 'project_completed'
        )
        .sort((a, b) => {
          const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
          const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
          return bDate - aDate;
        });
      
      setProjects(projectData);
      
    } catch (error) {
      console.error('Error fetching projects:', error);
      setError('Failed to connect to database. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  // Filter projects based on active filter
  useEffect(() => {
    let filtered = [...projects];
    
    switch (activeFilter) {
      case 'in_progress':
        filtered = projects.filter(p => p.status === 'project_started');
        break;
      case 'completed':
        filtered = projects.filter(p => p.status === 'project_completed');
        break;
      case 'all':
      default:
        filtered = projects;
        break;
    }
    
    setFilteredProjects(filtered);
  }, [projects, activeFilter]);

  useEffect(() => {
    fetchProjects();
  }, [jeweller]);

  const handleProjectCreated = () => {
    fetchProjects();
  };

  const handleCardClick = (projectId) => {
    navigate(`/projects/${projectId}`);
  };

  const updateProjectStatus = async (projectId, newStatus) => {
    setUpdatingStatus(prev => ({ ...prev, [projectId]: true }));
    
    try {
      const updateStatus = httpsCallable(functions, 'updateProjectStatus');
      await updateStatus({ projectId, newStatus });
      await fetchProjects();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [projectId]: false }));
    }
  };

  // Filter counts
  const filterCounts = {
    all: projects.length,
    in_progress: projects.filter(p => p.status === 'project_started').length,
    completed: projects.filter(p => p.status === 'project_completed').length
  };

  // Summary calculations
  const summaryData = {
    total: projects.length,
    inProgress: projects.filter(p => p.status === 'project_started').length,
    completed: projects.filter(p => p.status === 'project_completed').length,
    totalValue: projects.reduce((sum, p) => sum + (p.totalInvoiced || 0), 0)
  };

  if (loading) {
    return <ProjectsLoadingState />;
  }

  if (error) {
    return <ProjectsErrorState error={error} />;
  }

  return (
    <div className={styles.container}>
      <ProjectsHeader 
        showNewProjectMenu={showNewProjectMenu}
        setShowNewProjectMenu={setShowNewProjectMenu}
        setShowCreateModal={setShowCreateModal}
        walkthrough={walkthrough}
      />

      <ProjectsSummaryCards summaryData={summaryData} />

      <ProjectsFilters 
        activeFilter={activeFilter}
        onFilterChange={setActiveFilter}
        filterCounts={filterCounts}
      />

      {filteredProjects.length === 0 ? (
        <ProjectsEmptyState 
          activeFilter={activeFilter}
          onNavigateToEnquiries={() => navigate('/enquiries')}
        />
      ) : (
        <ProjectsGrid 
          projects={filteredProjects}
          onCardClick={handleCardClick}
          onUpdateStatus={updateProjectStatus}
          updatingStatus={updatingStatus}
          navigate={navigate}
        />
      )}

      <CreateProjectModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onProjectCreated={handleProjectCreated}
        title="Create New Project"
        description="Create a project that starts immediately"
      />
    </div>
  );
};

export default ProjectsPage;